/* eslint-disable react/destructuring-assignment */
import React, { useState, useContext } from 'react';
import { useForm } from '@abyss/ui/form/hooks/useForm';
import PropTypes from 'prop-types';
import { Flex } from '@abyss/ui/base/Flex';
import { Button } from 'src/common/Button';
import { Loading } from '@abyss/ui/base/Loading';
import { formatter } from 'src/tools/formatter';
import { styled } from '@abyss/ui/tools/styled';
import { useProviderPrivileges } from 'src/hooks/useProviderPrivileges';
import { AlertContext } from 'src/tools/AlertContext';

import { Corporates } from './Corporates';
import { TaxIdNumbers } from './TaxIdNumbers';
import { CareProviders } from './CareProviders';

const parseProvider = data => {
  const splitName = data.servicingProvider.name.split(', ');
  const servicingProviderFirstName = splitName[1];
  const servicingProviderLastName = splitName[0];

  return {
    organizationId: data.organization?.id,
    organizationName: formatter('capitalCase', data.organization?.name),
    corporateProviderMpin: data.corporateProvider.mpin,
    corporateProviderName: formatter(
      'capitalCase',
      data.corporateProvider.name
    ),
    corporateProviderLastName: formatter(
      'capitalCase',
      data.corporateProvider.name
    ),
    taxIdNumber: data.taxIdNumber.tin,
    servicingProviderMpin: data.servicingProvider.mpin,
    servicingProviderName: data.servicingProvider.name,
    servicingProviderFirstName: formatter(
      'capitalCase',
      servicingProviderFirstName
    ),
    servicingProviderLastName: formatter(
      'capitalCase',
      servicingProviderLastName
    ),
  };
};

const FormContainerDiv = styled.div({
  padding: 0,
});

const FormSpacingDiv = styled.div({
  padding: 0,
});

const ButtonWidth = styled.div({
  width: '100%',
});

const FlexContent = styled(Flex.Content)({
  textAlign: 'right',
});

const CustomPadding = styled.div({
  paddingTop: 15,
  borderTop: 'none',
});

export const ProviderSelect = ({ uuid }) => {
  const [selectedProvider, setSelectedProvider] = useState({});
  const [defaultProvider, setDefaultProvider] = useState({});
  const [allowProviderSearch, setAllowProviderSearch] = useState(true);
  const [organizations, setOrganizations] = useState([]);
  const form = useForm();
  const errorContext = useContext(AlertContext);
  const [, getOrganizations] = useProviderPrivileges({
    onCompleted: data => {
      setOrganizations(data);
      if (data.length === 1) {
        const firstTaxId = data[0];
        form.set('utility.taxIdNumber', firstTaxId);
        setSelectedProvider(prevProvider => {
          if (prevProvider.taxIdNumber === firstTaxId.tin) {
            return prevProvider;
          }
          return {
            ...selectedProvider,
            taxIdNumber: firstTaxId.tin,
            corporateProviderMpin: selectedProvider.corporateProviderMpin,
          };
        });
      }
    },
    onError: error => {
      errorContext.addAlert(error);
    },
  });

  const handleSave = () => {
    form.submit('utility', formData => {
      const { organization } = formData;

      if (organization && !organization.permissions) {
        const foundOrg = organizations.find(org => {
          return org.id === organization.organizationId;
        });
        const providerOrg = foundOrg || organizations[0];
        if (providerOrg) {
          organization.permissions = providerOrg.permissions || [];
        }
      }

      setDefaultProvider(formData);
      form.load('internalPreference', parseProvider(formData));
    });
  };

  const handleReset = () => {
    form.set('utility', defaultProvider);

    if (defaultProvider.organization) {
      setSelectedProvider({
        organizationId: defaultProvider.organization.id,
        corporateProviderMpin: defaultProvider.corporateProvider.mpin,
        taxIdNumber: defaultProvider.taxIdNumber.tin,
        firstProvider: defaultProvider.servicingProvider.mpin,
      });
    } else {
      setSelectedProvider({});
    }
  };

  const handleCancel = () => {
    handleReset();
  };

  return (
    <Loading
      label="Care Provider"
      // isLoading={
      //   // getSavedProvider.saga.isLoading || getOrganizations.saga.isLoading
      // }
      // errorMessage={
      //   // getSavedProvider.saga.errorMessage ||
      //   // getOrganizations.saga.errorMessage ||
      //   // getCorporates.saga.errorMessage ||
      //   // getTaxIdNumbers.saga.errorMessage ||
      //   // getProviders.saga.errorMessage
      // }
    >
      <FormContainerDiv data-testid="form-container">
        <Flex gutters={[6, 8]}>
          <Flex.Content space={100}>
            <FormSpacingDiv>
              <Corporates
                id="coporate-owner"
                uuid={uuid}
                selectedProvider={selectedProvider}
                setSelectedProvider={setSelectedProvider}
                setAllowProviderSearch={setAllowProviderSearch}
              />
            </FormSpacingDiv>
          </Flex.Content>
          <Flex.Content space={100}>
            <FormSpacingDiv>
              <TaxIdNumbers
                uuid={uuid}
                selectedProvider={selectedProvider}
                setSelectedProvider={setSelectedProvider}
                allowProviderSearch={allowProviderSearch}
                getOrganizations={getOrganizations}
                organizations={organizations}
              />
            </FormSpacingDiv>
          </Flex.Content>
          <Flex.Content space={100}>
            <FormSpacingDiv>
              <CareProviders
                uuid={uuid}
                selectedProvider={selectedProvider}
                setSelectedProvider={setSelectedProvider}
                allowProviderSearch={allowProviderSearch}
              />
            </FormSpacingDiv>
          </Flex.Content>
        </Flex>
      </FormContainerDiv>
      <CustomPadding>
        <Flex gutters={[8, 8]}>
          <FlexContent>
            <ButtonWidth>
              <Button
                id="provider-select-cancel-button"
                data-testid="provider-select-cancel-button"
                variant="outline"
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </ButtonWidth>
          </FlexContent>
          <Flex.Content>
            <ButtonWidth>
              <Button
                id="provider-select-save-button"
                data-testid="provider-select-save-button"
                data-cy="provider-select-save-button"
                onClick={handleSave}
                isDisabled={!allowProviderSearch}
              >
                Save
              </Button>
            </ButtonWidth>
          </Flex.Content>
        </Flex>
      </CustomPadding>
    </Loading>
  );
};

ProviderSelect.propTypes = {
  uuid: PropTypes.string,
};

ProviderSelect.defaultProps = {
  uuid: null,
};
